import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

/** Auth provider — uses email/password + JWT in httpOnly cookie. */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");   // loading | anon | ok
  const [errorMsg, setErrorMsg] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      setStatus("ok");
    } catch {
      setUser(null);
      setStatus("anon");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    setErrorMsg("");
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      setStatus("ok");
      return r.data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Error al iniciar sesión";
      setErrorMsg(typeof detail === "string" ? detail : JSON.stringify(detail));
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout silent:", e?.message); }
    setUser(null);
    setStatus("anon");
  }, []);

  return (
    <AuthCtx.Provider value={{ user, status, errorMsg, login, logout, refresh, setErrorMsg }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
